import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { useMemo } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { t } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { Grid, InputAdornment } from '@mui/material'

import DatePickerInput from '@/components/ui/inputs/DatePickerInput'
import InfoSection from '@/components/ui/sections/InfoSection'
import TextInput from '@/components/ui/inputs/TextInput'
import CheckboxInputController from '@/components/ui/inputs/CheckboxInputController'
import PhoneNumberInputController from '@/components/ui/inputs/PhoneNumberInputController'
import SelectInputController from '@/components/ui/inputs/SelectInputController'
import TextInputController from '@/components/ui/inputs/TextInputController'
import { useIsUS } from '@/hooks/useIsUS'
import { useIsSA } from '@/hooks/useIsSA'
import { useIsMobile } from '@/hooks/useSizeBreakpoints'
import { calculateAge, getGenderOptions } from '@/utils/patient'
import { toUtcMidnight } from '@/utils/dateUtils'

import { PatientProfileFormData } from '../model/usePatientProfileForm'
import { Mode } from '../model/types'
import { IDENTITY_NAME_MAX_LENGTH, sanitizeIdentityNameInput } from '../lib/identity-naming'

dayjs.extend(utc)

type GeneralInfoSectionProps = {
  editable: boolean
  isINSEnabled: boolean
  ssnLength: number
  mode: Mode
}

export function GeneralInfoSection({ editable, isINSEnabled = false, ssnLength, mode }: GeneralInfoSectionProps) {
  const { i18n } = useLingui()
  const isMobile = useIsMobile()
  const isUS = useIsUS()
  const isSA = useIsSA()
  const i18next = useTranslation()
  const rhForm = useFormContext<PatientProfileFormData>()
  const ignoreSecu = useWatch({ control: rhForm.control, name: 'ignoreSecu' })

  const xs = isMobile ? 12 : 6

  const genderOptions = useMemo(
    () =>
      isSA
        ? [
            { label: t`Male`, value: 'M' },
            { label: t`Female`, value: 'F' },
          ]
        : getGenderOptions().map((option) => ({ ...option, label: i18n._(option.label) })),
    [isSA, i18n, i18n.locale]
  )
  const nationalityOptions = useMemo(
    () => [
      { label: t`Saudi`, value: 'saudi' },
      { label: t`Non-Saudi`, value: 'non-saudi' },
    ],
    []
  )

  return (
    <InfoSection label={t`General information`}>
      <Grid container spacing={3} sx={{ mt: '0px' }}>
        {!isINSEnabled && (
          <Grid item xs={xs}>
            <TextInputController
              control={rhForm.control}
              id="name"
              fullWidth
              label={t`First name`}
              name="name"
              autoComplete="given-name"
              readOnly={!editable}
            />
          </Grid>
        )}
        {!isINSEnabled && (
          <Grid item xs={xs}>
            <TextInputController
              control={rhForm.control}
              id="surname"
              fullWidth
              label={t`Last name`}
              name="surname"
              autoComplete="family-name"
              readOnly={!editable}
            />
          </Grid>
        )}
        {!isINSEnabled && (
          <Grid item xs={isMobile ? 5 : 6}>
            <SelectInputController
              control={rhForm.control}
              id="gender"
              fullWidth
              label={t`Gender`}
              name="gender"
              options={genderOptions}
              readOnly={!editable}
            />
          </Grid>
        )}
        {!isINSEnabled && (
          <Grid item xs={isMobile ? 7 : 6}>
            <Controller
              control={rhForm.control}
              name="birthday"
              render={({ field, fieldState }) => (
                <DatePickerInput
                  disableFuture
                  fullWidth
                  name={field.name}
                  label={t`Birthday`}
                  onChange={(date) => {
                    rhForm.setValue('birthday', toUtcMidnight(date)?.toDate() ?? null)
                  }}
                  readOnly={!editable}
                  value={field.value && dayjs(field.value).isValid() ? dayjs(field.value) : null}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </Grid>
        )}
        {!isINSEnabled && mode === 'edit' && (
          <Grid item xs={xs}>
            <Controller
              control={rhForm.control}
              name="birthday"
              render={({ field }) => <TextInput fullWidth label={t`Age`} name="age" value={field.value ? calculateAge(field.value) : ''} readOnly />}
            />
          </Grid>
        )}
        {!isSA && (
          <Grid item xs={xs}>
            <TextInputController control={rhForm.control} id="email" fullWidth label={t`Email`} name="email" autoComplete="email" readOnly={!editable} />
          </Grid>
        )}
        <Grid item xs={xs}>
          <PhoneNumberInputController control={rhForm.control} label={isSA ? t`Contact Number` : t`Phone`} name="phone" readOnly={!editable} />
        </Grid>
        {isSA && (
          <Grid item xs={xs}>
            <SelectInputController
              control={rhForm.control}
              id="nationality"
              fullWidth
              label={t`Nationality`}
              name="nationality"
              options={nationalityOptions}
              readOnly={!editable}
            />
          </Grid>
        )}
        {editable && (
          <Grid item xs={12}>
            <CheckboxInputController
              control={rhForm.control}
              label={isSA ? t`Identification unavailable (Unknown)` : t`Patient has no social security number`}
              name="ignoreSecu"
            />
            {isUS ? (
              <TextInputController control={rhForm.control} disabled={ignoreSecu} fullWidth label={i18next.t('field.secu.label')} name="primaryPolicy" />
            ) : (
              <TextInputController
                control={rhForm.control}
                id="secu"
                fullWidth
                disabled={ignoreSecu}
                InputProps={{
                  // The 15-character SSN counter only applies to the French-style social number.
                  endAdornment: isSA ? undefined : <SecuIndicator ssnLength={ssnLength} />,
                }}
                label={isSA ? t`National ID / Iqama ID / Border Number` : t`Social number`}
                name="secu"
              />
            )}
          </Grid>
        )}
        {isUS && (
          <Grid item xs={12}>
            <TextInputController
              control={rhForm.control}
              fullWidth
              label={i18next.t('field.insuranceProvider.label')}
              name="insuranceProvider"
              readOnly={!editable}
            />
          </Grid>
        )}
        {editable && !isUS && !isSA && (
          <Grid item xs={12}>
            <TextInputController control={rhForm.control} id="pluginId" fullWidth label={t`Id`} name="pluginId" />
          </Grid>
        )}
      </Grid>
    </InfoSection>
  )
}

function SecuIndicator({ ssnLength }: { ssnLength: number }) {
  const rhForm = useFormContext<PatientProfileFormData>()
  const secu = useWatch({ control: rhForm.control, name: 'secu' })
  return <InputAdornment position="end" sx={{ mr: 1 }}>{`${secu?.length ?? 0}/${ssnLength}`}</InputAdornment>
}
